import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  Injectable,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectThemeToggle } from '@app/generated/feature-flags.selectors';
import { AppConfigService } from '../config/app-config.service';

export type ThemeMode = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'syrf-theme-mode';
export const THEME_TOGGLE_STORAGE_KEY = 'syrf-theme-toggle-enabled';
export const DARK_THEME_CLASS = 'global-dark-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _document = inject(DOCUMENT);
  private readonly _overlayContainer = inject(OverlayContainer);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _store = inject(Store, { optional: true });

  private readonly _storeThemeToggle = this._store
    ? toSignal(this._store.select(selectThemeToggle), {
        initialValue: AppConfigService.configLoaded
          ? AppConfigService.staticConfig.themeToggle
          : false,
      })
    : signal(
        AppConfigService.configLoaded
          ? AppConfigService.staticConfig.themeToggle
          : false
      );

  /** Whether the theme toggle feature is enabled */
  readonly isFeatureEnabled = computed<boolean>(() => {
    return this._storeThemeToggle() ?? false;
  });

  private readonly _mode = signal<ThemeMode>(this._loadInitialMode());
  private readonly _systemPrefersDark = signal<boolean>(
    this._getSystemPrefersDark()
  );

  /** Active mode setting: 'light', 'dark', or 'system' */
  readonly mode = this._mode.asReadonly();

  /** Whether dark mode is currently effective */
  readonly isDark = computed<boolean>(() => {
    if (!this.isFeatureEnabled()) {
      return false;
    }
    const currentMode = this._mode();
    if (currentMode === 'dark') return true;
    if (currentMode === 'light') return false;
    return this._systemPrefersDark();
  });

  /** Display label for the current mode */
  readonly modeLabel = computed<string>(() => {
    switch (this._mode()) {
      case 'dark':
        return 'Dark';
      case 'light':
        return 'Light';
      case 'system':
        return 'System';
    }
  });

  constructor() {
    this._initSystemListener();

    // Immediately apply theme state to document and overlay container synchronously
    this._applyTheme(this.isDark());

    // Synchronize the DOM and effective flag storage whenever theme state changes
    effect(() => {
      const dark = this.isDark();
      this._applyTheme(dark);
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(
            THEME_TOGGLE_STORAGE_KEY,
            this.isFeatureEnabled() ? 'true' : 'false'
          );
        }
      } catch {
        // Ignore storage errors
      }
    });
  }

  /**
   * Set explicit theme mode and persist to localStorage
   */
  setMode(mode: ThemeMode): void {
    this._mode.set(mode);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(THEME_STORAGE_KEY, mode);
      }
    } catch {
      // Ignore storage errors (e.g. private browsing mode)
    }
  }

  /**
   * Toggle between light and dark mode.
   * If currently in system mode, switches to opposite of effective state.
   */
  toggleTheme(): void {
    if (this.isDark()) {
      this.setMode('light');
    } else {
      this.setMode('dark');
    }
  }

  private _loadInitialMode(): ThemeMode {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
          return stored;
        }
      }
    } catch {
      // Ignore storage errors
    }
    return 'system';
  }

  private _getSystemPrefersDark(): boolean {
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function'
    ) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  }

  private _initSystemListener(): void {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      this._systemPrefersDark.set(e.matches);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handler);
      this._destroyRef.onDestroy(() => {
        mediaQuery.removeEventListener('change', handler);
      });
    } else if (typeof (mediaQuery as any).addListener === 'function') {
      (mediaQuery as any).addListener(handler);
      this._destroyRef.onDestroy(() => {
        (mediaQuery as any).removeListener(handler);
      });
    }
  }

  private _applyTheme(isDark: boolean): void {
    const docEl = this._document?.documentElement;
    const body = this._document?.body;

    if (docEl) {
      docEl.classList.remove('syrf-theme-pending');
      docEl.classList.toggle(DARK_THEME_CLASS, isDark);
      docEl.style.colorScheme = isDark ? 'dark' : 'light';
    }

    if (body) {
      body.classList.toggle(DARK_THEME_CLASS, isDark);
    }

    const overlayEl = this._overlayContainer?.getContainerElement();
    if (overlayEl) {
      overlayEl.classList.toggle(DARK_THEME_CLASS, isDark);
    }

    const metaThemeColor = this._document?.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDark ? '#121316' : '#ffffff');
    }
  }
}
